import React, { useMemo } from 'react';
import { Card, Space, Typography, Divider } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import './styles.css';

const { Title, Text } = Typography;

export const CampaignLaunchDetailsProgress = ({ campaigns, form }) => {
  // Scroll to a specific section smoothly
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  // Calculate progress for each campaign
  const campaignProgress = useMemo(() => {
    return campaigns.map((campaign, index) => {
      const formValues = form.getFieldValue('campaigns')?.[index] || {};

      // Check if all required DSP fields are filled
      const hasDSP = Boolean(formValues.demandSidePlatform);
      const hasMaxBid = formValues.maxBid !== null && formValues.maxBid !== undefined;

      // Check line items
      const lineItems = campaign.lineItems || [];
      const lineItemsProgress = lineItems.map((lineItem, lineItemIndex) => {
        const lineItemFormValues = formValues.lineItems?.[lineItemIndex] || {};

        const hasSSP = Boolean(lineItemFormValues.supplySidePlatform);
        const hasLineItemMaxBid = lineItemFormValues.maxBid !== null && lineItemFormValues.maxBid !== undefined;
        const hasFrequencyCap = lineItemFormValues.frequencyCap !== null && lineItemFormValues.frequencyCap !== undefined;

        const isComplete = hasSSP && hasLineItemMaxBid && hasFrequencyCap;

        return {
          name: lineItem.name || `Line Item ${lineItemIndex + 1}`,
          isComplete,
        };
      });

      const allLineItemsComplete = lineItemsProgress.every(item => item.isComplete);
      const campaignComplete = hasDSP && hasMaxBid && allLineItemsComplete;

      return {
        name: campaign.name || `Campaign ${index + 1}`,
        isComplete: campaignComplete,
        lineItems: lineItemsProgress,
      };
    });
  }, [campaigns, form]);

  return (
    <Card
      title={<Title level={5}>PROGRESS</Title>}
      className="campaign-launch-progress"
      style={{
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Details Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Details</Text>
        </div>

        {/* Duration & Budget Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Duration & Budget</Text>
        </div>

        {/* Categories Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Categories</Text>
        </div>

        {/* Attribution Reporting Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Attribution Reporting</Text>
        </div>

        {/* Campaigns */}
        {campaignProgress.map((campaign, index) => (
          <div key={index}>
            <Divider style={{ margin: '8px 0' }} />
            <div
              onClick={() => scrollToSection(`campaign-${index}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '4px 0',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <CheckCircleOutlined
                style={{
                  color: campaign.isComplete ? '#52c41a' : '#d9d9d9',
                  fontSize: 16,
                }}
              />
              <Text strong={!campaign.isComplete}>{campaign.name}</Text>
            </div>

            {/* Line Items under each campaign */}
            {campaign.lineItems.map((lineItem, lineItemIndex) => (
              <div
                key={lineItemIndex}
                onClick={() => scrollToSection(`campaign-${index}-lineitem-${lineItemIndex}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  paddingLeft: 24,
                  marginTop: 4,
                  cursor: 'pointer',
                  padding: '4px 0 4px 24px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <CheckCircleOutlined
                  style={{
                    color: lineItem.isComplete ? '#52c41a' : '#d9d9d9',
                    fontSize: 14,
                  }}
                />
                <Text
                  type={lineItem.isComplete ? 'secondary' : undefined}
                  strong={!lineItem.isComplete}
                >
                  {lineItem.name}
                </Text>
              </div>
            ))}
          </div>
        ))}
      </Space>
    </Card>
  );
};
